import React, { useState, useEffect, useMemo } from 'react';
import {
  Bell,
  Plus,
  Send,
  CheckCircle2,
  AlertCircle,
  Users,
  Search,
  Filter,
  X,
  Clock,
} from 'lucide-react';
import { db } from '../../firebase/config/firebase';
import { collection, getDocs, setDoc, doc, query, orderBy } from 'firebase/firestore';
import { AdminLoadMore } from './AdminLoadMore';
import './AdminPages.css';

interface BroadcastNotification {
  id: string;
  title: string;
  message: string;
  targetAudience: 'ALL' | 'ALEX_ONLY' | 'DELTAS' | 'CAIRO';
  audienceLabel: string;
  priority: 'NORMAL' | 'HIGH' | 'URGENT';
  sentAt: string;
  sentBy: string;
  recipientCount: number;
}

const INITIAL_NOTIFICATIONS: BroadcastNotification[] = [
  {
    id: 'notif_01',
    title: 'تأكيد مواعيد رحلات بداية الأسبوع',
    message: 'نحيطكم علماً بأن كافة رحلات الذهاب يوم الأحد القادم ستنطلق في مواعيدها المحددة تماماً. يرجى التواجد قبل الموعد بـ 15 دقيقة.',
    targetAudience: 'ALL',
    audienceLabel: 'جميع الطلاب المسجلين',
    priority: 'NORMAL',
    sentAt: '2026-10-04 09:00 ص',
    sentBy: 'مدير العمليات',
    recipientCount: 420,
  },
  {
    id: 'notif_02',
    title: 'تحديث مسار رحلات جامعة كفر الشيخ',
    message: 'تم تحديث نقطة التجمع البديلة بجوار محطة سيدي جابر لرحلات كفر الشيخ لتفادي أعمال الصيانة بالطريق.',
    targetAudience: 'DELTAS',
    audienceLabel: 'طلاب جامعات الدلتا وكفر الشيخ',
    priority: 'HIGH',
    sentAt: '2026-10-03 06:30 م',
    sentBy: 'إدارة النقل',
    recipientCount: 88,
  },
  {
    id: 'notif_03',
    title: 'فتح باب حجز رحلات امتحانات منتصف الفصل',
    message: 'تم فتح جدول رحلات إضافية مخصصة لفترة الميدتيرم لمختلف الجامعات مع توفير مقاعد إضافية في تويوتا هايس.',
    targetAudience: 'ALL',
    audienceLabel: 'جميع الطلاب المسجلين',
    priority: 'NORMAL',
    sentAt: '2026-10-01 12:00 م',
    sentBy: 'الدعم الطلابي',
    recipientCount: 420,
  },
];

export const AdminNotifications: React.FC = () => {
  const [notifications, setNotifications] = useState<BroadcastNotification[]>(INITIAL_NOTIFICATIONS);
  const [searchTerm, setSearchTerm] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('ALL');
  const [visibleCount, setVisibleCount] = useState<number>(15);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [actionSuccess, setActionSuccess] = useState('');
  const [actionLoading, setActionLoading] = useState(false);

  useEffect(() => {
    const fetchNotifications = async () => {
      try {
        const notifRef = collection(db, 'broadcastNotifications');
        const q = query(notifRef, orderBy('sentAt', 'desc'));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const list: BroadcastNotification[] = [];
          snap.forEach((docSnap) => {
            list.push({ id: docSnap.id, ...(docSnap.data() as any) });
          });
          setNotifications(list);
        }
      } catch (err) {
        console.warn('Could not load notifications from Firestore, using initial:', err);
      }
    };
    fetchNotifications();
  }, []);

  // Form state
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    targetAudience: 'ALL' as 'ALL' | 'ALEX_ONLY' | 'DELTAS' | 'CAIRO',
    priority: 'NORMAL' as 'NORMAL' | 'HIGH' | 'URGENT',
  });

  const filteredNotifications = notifications.filter((notif) => {
    const matchesSearch =
      notif.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notif.message.toLowerCase().includes(searchTerm.toLowerCase()) ||
      notif.sentBy.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesPriority = priorityFilter === 'ALL' || notif.priority === priorityFilter;
    return matchesSearch && matchesPriority;
  });

  // Reset pagination on filter change
  useEffect(() => {
    setVisibleCount(15);
  }, [searchTerm, priorityFilter]);

  const visibleNotifications = useMemo(() => {
    return filteredNotifications.slice(0, visibleCount);
  }, [filteredNotifications, visibleCount]);


  const handleSendNotification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.title.trim() || !formData.message.trim()) {
      alert('يرجى كتابة عنوان الإشعار ونص الرسالة');
      return;
    }

    setActionLoading(true);
    try {
      let audienceLabel = 'جميع الطلاب المسجلين';
      let recipientCount = 420;

      if (formData.targetAudience === 'DELTAS') {
        audienceLabel = 'طلاب جامعات الدلتا وكفر الشيخ';
        recipientCount = 115;
      } else if (formData.targetAudience === 'CAIRO') {
        audienceLabel = 'طلاب جامعات القاهرة وعين شمس وحلوان';
        recipientCount = 190;
      } else if (formData.targetAudience === 'ALEX_ONLY') {
        audienceLabel = 'طلاب الإسكندرية والساحل';
        recipientCount = 115;
      }

      const now = new Date();
      const timeStr = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(
        now.getDate()
      ).padStart(2, '0')} ${String(now.getHours()).padStart(2, '0')}:${String(
        now.getMinutes()
      ).padStart(2, '0')}`;

      const newNotif: BroadcastNotification = {
        id: `notif_${Date.now()}`,
        title: formData.title.trim(),
        message: formData.message.trim(),
        targetAudience: formData.targetAudience,
        audienceLabel,
        priority: formData.priority,
        sentAt: timeStr,
        sentBy: 'مدير النظام (أنت)',
        recipientCount,
      };

      try {
        await setDoc(doc(db, 'broadcastNotifications', newNotif.id), newNotif);
      } catch (err) {
        console.warn('Saved in memory (firestore write failed):', err);
      }

      setNotifications([newNotif, ...notifications]);
      setIsModalOpen(false);
      setFormData({
        title: '',
        message: '',
        targetAudience: 'ALL',
        priority: 'NORMAL',
      });
      setActionSuccess(`تم إرسال الإشعار بنجاح إلى ${recipientCount} طالباً!`);
      setTimeout(() => setActionSuccess(''), 5000);
    } finally {
      setActionLoading(false);
    }
  };

  return (
    <div className="admin-page-container" dir="rtl">
      {/* Header */}
      <div className="admin-page-header">
        <div>
          <h2>الإشعارات والتعميمات الجماعية</h2>
          <p className="admin-page-subtitle">
            إرسال إشعارات وتنبيهات فورية لجميع الطلاب أو لركاب جامعات ومسارات محددة
          </p>
        </div>
        <div className="admin-page-actions">
          <button onClick={() => setIsModalOpen(true)} className="btn-primary flex items-center gap-2">
            <Plus size={16} />
            <span>إرسال إشعار جديد</span>
          </button>
        </div>
      </div>

      {actionSuccess && (
        <div className="admin-alert-banner success">
          <CheckCircle2 size={18} />
          <span>{actionSuccess}</span>
        </div>
      )}

      {/* Stats Cards */}
      <div className="admin-stats-grid">
        <div className="admin-stat-card">
          <div className="stat-card-header">
            <span className="stat-title">إجمالي التعميمات</span>
            <span className="stat-icon purple">
              <Bell size={20} />
            </span>
          </div>
          <div className="stat-value">{notifications.length}</div>
          <div className="stat-desc">إشعار جماعي تم إرساله</div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-card-header">
            <span className="stat-title">الطلاب المتصلين</span>
            <span className="stat-icon green">
              <Users size={20} />
            </span>
          </div>
          <div className="stat-value">420+</div>
          <div className="stat-desc">طالب يستلمون الإشعارات الفورية</div>
        </div>

        <div className="admin-stat-card">
          <div className="stat-card-header">
            <span className="stat-title">تنبيهات هامة</span>
            <span className="stat-icon red">
              <AlertCircle size={20} />
            </span>
          </div>
          <div className="stat-value">
            {notifications.filter((n) => n.priority === 'HIGH' || n.priority === 'URGENT').length}
          </div>
          <div className="stat-desc">تنبيهات عاجلة وطارئة</div>
        </div>
      </div>

      {/* Filters Toolbar */}
      <div className="admin-toolbar">
        <div className="admin-search-box">
          <Search size={18} className="search-icon" />
          <input
            type="text"
            placeholder="بحث في الإشعارات بالموضوع أو المحتوى أو المرسل..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>

        <div className="admin-filters-group">
          <div className="filter-select-wrapper">
            <Filter size={16} className="filter-icon" />
            <select
              value={priorityFilter}
              onChange={(e) => setPriorityFilter(e.target.value)}
              className="admin-select"
            >
              <option value="ALL">جميع المستويات</option>
              <option value="NORMAL">عادي</option>
              <option value="HIGH">هام</option>
              <option value="URGENT">عاجل جداً</option>
            </select>
          </div>
        </div>
      </div>

      {/* Notifications Cards List */}
      <div className="space-y-4">
        {filteredNotifications.length === 0 ? (
          <div className="admin-empty-state">
            <Bell size={48} className="empty-icon" />
            <p>لا توجد إشعارات مطابقة لمعايير البحث</p>
          </div>
        ) : (
          <>
            {visibleNotifications.map((notif) => (
              <div
                key={notif.id}
                className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm hover:shadow-md transition-shadow relative overflow-hidden"
                style={{
                  borderRight: `5px solid ${
                    notif.priority === 'URGENT'
                      ? '#ef4444'
                      : notif.priority === 'HIGH'
                      ? '#f59e0b'
                      : '#3b82f6'
                  }`,
                }}
              >
                <div className="flex items-start justify-between gap-4 mb-2">
                  <div className="flex items-center gap-3">
                    <span
                      className="p-2 rounded-lg"
                      style={{
                        backgroundColor:
                          notif.priority === 'URGENT'
                            ? '#fee2e2'
                            : notif.priority === 'HIGH'
                            ? '#fef3c7'
                            : '#eff6ff',
                        color:
                          notif.priority === 'URGENT'
                            ? '#dc2626'
                            : notif.priority === 'HIGH'
                            ? '#d97706'
                            : '#2563eb',
                      }}
                    >
                      <Bell size={20} />
                    </span>
                    <div>
                      <h3 className="font-bold text-slate-800 text-base">{notif.title}</h3>
                      <div className="flex items-center gap-3 text-xs text-slate-500 mt-1">
                        <span className="flex items-center gap-1">
                          <Clock size={13} />
                          {notif.sentAt}
                        </span>
                        <span>•</span>
                        <span>بواسطة: {notif.sentBy}</span>
                      </div>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <span
                      className={`status-pill ${
                        notif.priority === 'URGENT'
                          ? 'cancelled'
                          : notif.priority === 'HIGH'
                          ? 'pending'
                          : 'confirmed'
                      }`}
                    >
                      {notif.priority === 'URGENT'
                        ? 'عاجل جداً'
                        : notif.priority === 'HIGH'
                        ? 'هام'
                        : 'عادي'}
                    </span>
                  </div>
                </div>

                <p className="text-slate-700 text-sm leading-relaxed mt-3 mb-4 bg-slate-50 p-3.5 rounded-lg border border-slate-100">
                  {notif.message}
                </p>

                <div className="flex flex-wrap items-center justify-between text-xs text-slate-500 pt-2 border-t border-slate-100 gap-2">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-slate-700">الفئة المستهدفة:</span>
                    <span className="bg-slate-100 text-slate-700 px-2 py-0.5 rounded font-medium">
                      {notif.audienceLabel}
                    </span>
                  </div>
                  <div className="flex items-center gap-1 text-slate-600 font-medium">
                    <Users size={14} />
                    <span>وصل إلى: {notif.recipientCount} طالباً</span>
                  </div>
                </div>
              </div>
            ))}

            {filteredNotifications.length > 0 && (
              <AdminLoadMore
                currentCount={Math.min(visibleCount, filteredNotifications.length)}
                totalCount={filteredNotifications.length}
                itemName="إشعار"
                onLoadMore={() => setVisibleCount((prev) => prev + 10)}
                step={10}
                onShowAll={() => setVisibleCount(filteredNotifications.length)}
              />
            )}
          </>
        )}
      </div>

      {/* Send Notification Modal */}
      {isModalOpen && (
        <div className="admin-modal-overlay">
          <div className="admin-modal-card" style={{ maxWidth: '600px' }}>
            <div className="admin-modal-header">
              <div className="flex items-center gap-2">
                <Send size={20} className="text-[#E5232A]" />
                <h3>إرسال إشعار جماعي جديد</h3>
              </div>
              <button onClick={() => setIsModalOpen(false)} className="close-btn">
                <X size={18} />
              </button>
            </div>

            <form onSubmit={handleSendNotification} className="admin-modal-body">
              <div className="form-group">
                <label>عنوان الإشعار *</label>
                <input
                  type="text"
                  required
                  placeholder="مثال: تنبيه هام بخصوص مواعيد رحلات الأحد"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group flex-1">
                  <label>الفئة المستهدفة *</label>
                  <select
                    value={formData.targetAudience}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        targetAudience: e.target.value as any,
                      })
                    }
                    className="form-control"
                  >
                    <option value="ALL">جميع الطلاب المسجلين (420 طالب)</option>
                    <option value="DELTAS">طلاب جامعات الدلتا وكفر الشيخ (115 طالب)</option>
                    <option value="CAIRO">طلاب جامعات القاهرة وعين شمس (190 طالب)</option>
                    <option value="ALEX_ONLY">طلاب الإسكندرية والساحل (115 طالب)</option>
                  </select>
                </div>

                <div className="form-group flex-1">
                  <label>مستوى الأهمية *</label>
                  <select
                    value={formData.priority}
                    onChange={(e) =>
                      setFormData({
                        ...formData,
                        priority: e.target.value as any,
                      })
                    }
                    className="form-control"
                  >
                    <option value="NORMAL">عادي (إشعار يومي روتيني)</option>
                    <option value="HIGH">هام (تحديث مسار أو توقيت)</option>
                    <option value="URGENT">عاجل جداً (تنبيه طارئ)</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label>نص الرسالة / الإشعار *</label>
                <textarea
                  required
                  rows={4}
                  placeholder="اكتب تفاصيل الإشعار بوضوح للطلاب..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="form-control"
                />
              </div>

              <div className="bg-rose-50 border border-rose-200 rounded-lg p-3 text-xs text-rose-800 flex items-start gap-2">
                <AlertCircle size={16} className="flex-shrink-0 mt-0.5 text-[#E5232A]" />
                <span>
                  سيظهر هذا الإشعار فوراً في شريط التنبيهات وبوابة الطالب لجميع الحسابات المشمولة في
                  الفئة المستهدفة.
                </span>
              </div>

              <div className="admin-modal-footer">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="btn-secondary"
                  disabled={actionLoading}
                >
                  إلغاء
                </button>
                <button
                  type="submit"
                  className="btn-primary flex items-center gap-2"
                  disabled={actionLoading}
                >
                  <Send size={16} />
                  <span>{actionLoading ? 'جارٍ الإرسال...' : 'إرسال الإشعار الآن'}</span>
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminNotifications;
